import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DeleteOutlined, EditOutlined, VisibilityOutlined } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import CustomPagination from '../../../components/customPagination';
import { useGetRolesHandler } from '../dataHandlers/getRolesHandler';
import { useDeleteRoleHandler } from '../dataHandlers/deleteRoleHandler';
import RoleFormModal from './RoleFormModal';
import RoleDeleteDialog from './RoleDeleteDialog';

export default function RolesTable({ searchQuery = {}, handlePaginationChange }) {

  const { roles, totalElements, isLoading } = useGetRolesHandler({ searchQuery });

  const [modalMode, setModalMode] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { mutate: deleteRole, isPending: isDeleting } = useDeleteRoleHandler({
    onSuccess: () => {
      setDeleteOpen(false);
      setSelectedRole(null);
    },
  });

  const openModal = (mode, role) => {
    setSelectedRole(role);
    setModalMode(mode);
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedRole(null);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.4,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
          <Tooltip title={ 'View'}>
            <IconButton size="small" onClick={() => openModal('view', row)} aria-label={'View'}>
              <VisibilityOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Edit'}>
            <IconButton size="small" onClick={() => openModal('edit', row)} aria-label={'Edit'}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Delete'}>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedRole(row);
                setDeleteOpen(true);
              }}
              aria-label={'Delete'}
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box>
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={totalElements}
          loading={isLoading}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={44}
          columnHeaderHeight={40}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeader': {
              py: 0,
            },
          }}
          paginationModel={{
            page: searchQuery.page ?? 0,
            pageSize: searchQuery.size ?? 10,
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            handlePaginationChange(model.page, model.pageSize);
          }}
          slots={{ pagination: CustomPagination }}
        />
      </Box>

      {modalMode && (
        <RoleFormModal open={Boolean(modalMode)} onClose={closeModal} mode={modalMode} roleData={selectedRole} />
      )}

      <RoleDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        roleData={selectedRole}
        isDeleting={isDeleting}
        onSubmit={() => selectedRole && deleteRole(selectedRole.id)}
      />
    </>
  );
}
